import config from "appConfig";

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>In this Lesson, we will focus on acoustic waves propagating in air. A short presentation of the necessary notions for this electroacoustic course will be given. For those readers who wish to learn more about this field, we recommend the fundamentals of acoustics course available on the ASKnow platform or references [1, 2, 3].<br class=newline>Here, a presentation of the physical quantities related to the propagation of guided waves will be given. We will see that under certain conditions the behaviour of acoustic waveguides can be modelled using simple electrical circuits, hence introducing the electroacoustical analogies and lumped-element model.<br class=newline>
<h3 class=sectionHead>Acoustic quantities</h3>
<p>To characterise an acoustic wave, the following quantities are usually used: pressure and particle velocity.
<ul class=itemize1>
   <li class=itemize>
      The acoustic pressure
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>p</mi>
      </math>
      corresponds to the rapid fluctuations of the atmospheric pressure
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
      </math>
      (see <a href="#" data-img-alt="Figure 1.
      Acoustic pressure
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
      <mrow>
      <mi>p</mi>
      </mrow>
      <mrow>
      <mi>t</mi>
      </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      </math>
      fluctuating around the atmospheric pressure
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
      <mrow>
      <mi>p</mi>
      </mrow>
      <mrow>
      <mn>0</mn>
      </mrow>
      </msub>
      </math>." data-img-src=${config.ASSETS_URL}/04/04/theory/figure-1.svg>Figure 1</a>). It is a scalar quantity expressed in Pascal (Pa). The total pressure at
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
      </math>
      is then given by:
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <mi>p</mi>
      </math>. However, acousticians generally focus on the variable part.
   <li class=itemize>
      When an acoustic wave propagates in the air, it sets the fluid particles in vibration around their rest position at the velocity
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>V</mi></math>. A particle corresponds to a very small amount of fluid in which the physical quantities of interest can be considered as homogeneous. The velocity is expressed in m/s.
</ul>
<figure class=figure>
   <img alt="Figure 1.
      Acoustic pressure
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
      <mrow>
      <mi>p</mi>
      </mrow>
      <mrow>
      <mi>t</mi>
      </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      </math>
      fluctuating around the atmospheric pressure
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
      <mrow>
      <mi>p</mi>
      </mrow>
      <mrow>
      <mn>0</mn>
      </mrow>
      </msub>
      </math>." src=${config.ASSETS_URL}/04/04/theory/figure-1.svg>
   <figcaption class=caption>
      Figure 1.
      Acoustic pressure
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
         <mo class=MathClass-open>(</mo>
         <mi>t</mi>
         <mo class=MathClass-close>)</mo>
      </math>
      fluctuating around the atmospheric pressure
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
      </math>.
   </figcaption>
</figure>
<p>Sound pressure is often expressed in decibels, this relative unit allows:
<ul class=itemize1>
   <li class=itemize>To better quantify the perception of sound by humans, which is more related to the logarithm of the perceived intensity.
   <li class=itemize>
      To obtain a more comprehensible scale because the acoustic pressures encountered in everyday life can vary in very large proportions (
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mo class=MathClass-rel>></mo>
         <mn>1</mn>
         <msup>
            <mrow>
               <mn>0</mn>
            </mrow>
            <mrow>
               <mn>10</mn>
            </mrow>
         </msup>
         <mo class=MathClass-close>)</mo>
      </math>.
</ul>
<p>The sound pressure level in dB SPL (for Sound Pressure Level) is then given by
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>20</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mstyle class=text>
                           <mtext>RMS</mtext>
                        </mstyle>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mstyle class=text>
                           <mtext>ref</mtext>
                        </mstyle>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mstyle class=mbox>
               <mtext> (dB SPL)</mtext>
            </mstyle>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mstyle class=text>
               <mtext>RMS</mtext>
            </mstyle>
         </mrow>
      </msub>
   </math>
   is the Root Mean Square (RMS) value of the acoustic pressure and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mstyle class=text>
               <mtext>ref</mtext>
            </mstyle>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mo class=MathClass-bin>×</mo>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>5</mn>
         </mrow>
      </msup>
   </math>
   Pa is the reference sound pressure, which corresponds to the auditory threshold at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
   </math>
   kHz.<br class=newline>Acoustic waves propagate in non-dissipative fluids at a speed that does not depend on the frequency. This velocity is generally referred to as the celerity and is symbolised by the letter
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>c</mi>
   </math>. Be careful not to confuse the velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
   </math>
   which corresponds to the speed of vibration of the fluid particles with the sound velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>c</mi>
   </math>
   which corresponds to the speed of propagation of sound waves.<br class=newline>Let us consider a pure sinusoidal sound of frequency
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
   </math>
   propagating along a direction in space, for example the axis <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mo>(</mo>
      <mi mathvariant=italic>Ox</mi><mo>)</mo></math>. The shape of the sound pressure along this axis, at a fixed time
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>t</mi>
   </math>, is that of a sinusoidal function (see <a href="#" data-img-alt="Figure 2. Wavelength of a sinusoidal signal." data-img-src=${config.ASSETS_URL}/04/04/theory/figure-4.svg>Figure 2</a>). The wavelength is then defined as the spatial equivalent of the time period. It is the smallest distance for which, at a fixed time
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>t</mi>
   </math>, the sound wave is reproduced identically. The wavelength also corresponds to the distance travelled by an acoustic wave during the period
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>T</mi>
   </math>. As the wave propagates at the celerity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>c</mi>
   </math>, we obtain the following relationship between the various associated quantities
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>λ</mi>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>cT</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   For sounds in the audible range, the wavelength varies significantly: from
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>17</mn>
   </math>
   m at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>20</mn>
   </math>
   Hz to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>17</mn>
   </math>
   mm at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>20</mn>
   </math>
   kHz. It can be seen that the wavelength can therefore be very large or very small in relation to the size of everyday objects.
   <figure class=figure>
<p><img alt="Figure 2. Wavelength of a sinusoidal signal." src=${config.ASSETS_URL}/04/04/theory/figure-4.svg><figcaption class=caption>Figure 2. Wavelength of a sinusoidal signal.</figcaption></figure>
<h3 class=sectionHead>1D Wave equation</h3>
<p>
   Many electroacoustic systems have cavities, tubes, etc. through which the sound waves are guided. When the dimensions of these objects are small compared to the wavelength, the sound can be considered to propagate in one direction only. This behaviour is referred to in the scientific literature as “plane wave”. Thus, for all these elements: loudspeaker enclosure, rear cavity of microphone, equalization tube we will use a one-dimensional formalism. For instance, for a floorstanding tower speaker with a width of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>25</mn>
   </math> cm, the 1D assumption is relevant up to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>2</mn>
      <mi mathvariant=italic>πd</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>λ</mi>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
      <mo class=MathClass-rel>→</mo>
      <mi>λ</mi>
      <mo class=MathClass-rel>></mo>
      <mn>2</mn>
      <mi mathvariant=italic>πd</mi>
   </math>, giving a maximum frequency of
   <span style="font-size: 14px;">
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mstyle class=text>
                  <mtext>max</mtext>
               </mstyle>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mi>c</mi>
         <mo class=MathClass-bin>∕</mo>
         <mi>λ</mi>
         <mo class=MathClass-rel>=</mo>
         <mi>c</mi>
         <mo class=MathClass-bin>∕</mo>
         <mo class=MathClass-open>(</mo>
         <mn>2</mn>
         <mi mathvariant=italic>πd</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mn>342</mn>
         <mo class=MathClass-bin>∕</mo>
         <mo class=MathClass-open>(</mo>
         <mn>2</mn>
         <mi>π</mi>
         <mn>0</mn>
         <mo class=MathClass-punc>.</mo>
         <mn>25</mn>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mn>217</mn>
      </math>
   </span>
   Hz. The propagation approximation along the main direction of the speaker can therefore be used up to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>217</mn>
   </math>
   Hz. Above this frequency a 3-dimensional propagation model will be required. Note that for microphones that have much smaller dimensions, the 1D propagation model will be valid up to much higher frequencies.<br class=newline>
<p>
   For a plane wave travelling along the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>
   direction, the equation governing the propagation of sound is
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>p</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>k</mi>
   </math>
   the wavenumber is given by
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>ω</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>c</mi>
   </math>. The solution for a pure tone is given by
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>p</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>±</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   is the amplitude of the pressure wave. The
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>+</mo>
   </math>
   sign corresponds to a wave propagating in the direction of decreasing
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>
   while the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
   </math>
   sign corresponds to a wave propagating in the direction of increasing
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>. The particle velocity is given by
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>V</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the air volumetric mass density and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
   </math>
   is the air characteristic impedance, which represents the opposition of the propagation medium to the wave motion. Note that, for a plane wave,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
   </math>
   is proportional to the pressure
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
   </math>: these two quantities are therefore in phase.
<h3 class=sectionHead>Acoustic impedance</h3>
<p>
   When dealing with waveguides, the flow rate
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>
   through a waveguide section is generally used instead of the velocity using
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>Q</mi>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>SV</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>S</mi>
   </math>
   is the cross-sectional area of the waveguide. The acoustic impedance is then defined as the ratio of pressure
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
   </math>
   to volume velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>Q</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-open>(</mo>
            <msup>
               <mrow>
                  <mstyle class=text>              
                     <mtext> Pa</mtext>
                     <mstyle class=math>
                        <mo class=MathClass-bin>⋅</mo>
                     </mstyle>
                     <mtext>s/m</mtext>
                  </mstyle>
               </mrow>
               <mrow>
                  <mn>3</mn>
               </mrow>
            </msup>
            <mo>)</mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   In a waveguide (see <a href="#" data-img-alt="Figure 3. Geometry of the waveguide of interest." data-img-src=${config.ASSETS_URL}/04/04/theory/figure-3.svg>Figure 3</a>), it is possible to calculate its input acoustic impedance as a function of the acoustic impedance at a larger distance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>l</mi>
   </math>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>in</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>l</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>∕</mo>
                  <mi>S</mi>
                  <mi class=qopname>tan</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kl</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>l</mi>
                  <mo class=MathClass-close>)</mo>
                  <mi>S</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
                  <mi class=qopname>tan</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kl</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(8)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 3. Geometry of the waveguide of interest." src=${config.ASSETS_URL}/04/04/theory/figure-3.svg>
      <figcaption class=caption>Figure 3. Geometry of the waveguide of interest.</figcaption>
</figure>
<p>This can be very useful for calculating the unknown impedance at one position as a function of a known impedance at another position. This will be illustrated for simple waveguides in the next section.
<h3 class=sectionHead>Simple waveguides</h3>
<h4 class=subsectionHead>Closed tube</h4>
<p>
   Let’s now look at some simple waveguides, starting with the tube closed at one end depicted in <a href="#" data-img-alt="Figure 4. Closed tube and its electrical equivalent." data-img-src=${config.ASSETS_URL}/04/04/theory/figure-4.svg>Figure 4</a>. Here, the air is trapped in the closed volume and therefore acts like a spring when it is compressed. Think of the effect of blocking the end of a bicycle pump as you are pushing it.<br class=newline>The termination of the tube at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>l</mi>
   </math>
   is rigid, imposing a zero flow rate and therefore an infinite acoustic impedance.
   <figure class=figure>
<p><img alt="Figure 4. Closed tube and its electrical equivalent." src=${config.ASSETS_URL}/04/04/theory/figure-4.svg><figcaption class=caption>Figure 4. Closed tube and its electrical equivalent.</figcaption></figure>
<p>
   The input impedance of the closed tube
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>in</mi>
         </mrow>
      </msubsup>
   </math>
   can then be calculated by using
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>l</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>∞</mi>
   </math>
   in Eq. (8)
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>in</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>l</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>∕</mo>
                  <mi>S</mi>
                  <mi class=qopname>tan</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kl</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>l</mi>
                  <mo class=MathClass-close>)</mo>
                  <mi>S</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
                  <mi class=qopname>tan</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kl</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jS</mi>
                  <mi class=qopname>tan</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kl</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   At low frequencies, <em><span class=cmti-10>i.e.</span></em> when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>λ</mi>
      <mo class=MathClass-rel>≫</mo>
      <mi>l</mi>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>tan</mi>
      <mo>⁡</mo>
      <mi mathvariant=italic>kl</mi>
      <mo class=MathClass-rel>≃</mo>
      <mi mathvariant=italic>kl</mi>
   </math>, then
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>in</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>≃</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jSkl</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mi>o</mi>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωSl</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mi>o</mi>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>Sl</mi>
   </math>
   is the volume of the tube. The above equation can be rewritten in the following generic form
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>in</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mi>o</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </mfrac>
   </math>. Note that the input acoustic impedance of a closed tube has the same form as the one of a capacitor. Therefore, its behaviour can be modelled by the equivalent electrical circuit of <a href="#" data-img-alt="Figure 4. Closed tube and its electrical equivalent." data-img-src=${config.ASSETS_URL}/04/04/theory/figure-4.svg>Figure 4</a>.<br class=newline>At low frequencies, the impedance of a closed tube is inversely proportional to its volume. This property can be extended to any closed cavity/box of any shape, as long as the wavelength is large compared to the dimensions of the given volume. This is directly applicable in the modelling of the behaviour of loudspeaker enclosures.
<h4 class=subsectionHead>Open tube</h4>
<p>
   Now let’s move to the open tube of <a href="#" data-img-alt="Figure 5. Open tube and its electrical equivalent." data-img-src=${config.ASSETS_URL}/04/04/theory/figure-5.svg>Figure 5</a>. Due to the rapid change in section at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>l</mi>
   </math>, most of the acoustic energy is reflected in the tube, the acoustic pressure just outside the tube
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>l</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is then almost equal to zero and so is the impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>l</mi>
      <mo class=MathClass-close>)</mo>
   </math>. Then, by using
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>l</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>≃</mo>
      <mn>0</mn>
   </math>
   in Eq. (8), we get for the open tube
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>in</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
                  <mi class=qopname>tan</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kl</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(12)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 5. Open tube and its electrical equivalent." src=${config.ASSETS_URL}/04/04/theory/figure-5.svg>
      <figcaption class=caption>Figure 5. Open tube and its electrical equivalent.</figcaption>
</figure>
<p>
   At low frequencies,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>in</mi>
         </mrow>
      </msubsup>
   </math>
   takes the following form
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>in</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>≃</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ckl</mi>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>l</mi>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(13)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>S</mi>
         </mrow>
      </mfrac>
   </math>
   is the acoustic mass of the tube. The open tube has an acoustic impedance in the form of an inductor (see <a href="#" data-img-alt="Figure 5. Open tube and its electrical equivalent." data-img-src=${config.ASSETS_URL}/04/04/theory/figure-5.svg>Figure 5</a>). Here the air in the tube moves as if it were a single compact object.<br class=newline>In practice, there is a transition zone at both ends of the tube in which the 1D particle velocity changes from an axial particle vibration to a 3D velocity field. In this zone, there is length
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   outside of the tube in which the particle velocity remains mainly axial. Thus, from an acoustic point of view, the tube is longer than its physical length. Therefore, not only the air in the tube that moves but there also some of the air around the opening that move along. How much air it is that moves along depends on the geometry of the termination.<br class=newline>
<p>
   The additional length can be calculated for two simple configurations at low frequencies (<em><span class=cmti-10>i.e.</span></em> for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>λ</mi>
      <mo class=MathClass-rel>≫</mo>
      <msqrt>
         <mrow>
            <mi>S</mi>
            <mo class=MathClass-bin>∕</mo>
            <mi>π</mi>
         </mrow>
      </msqrt>
   </math>):
<ul class=itemize1>
   <li class=itemize>
      The end of the tube opens into a very large planar baffle: in that case,
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mi>c</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mi>b</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mn>0</mn>
         <mo class=MathClass-punc>.</mo>
         <mn>85</mn>
         <mi>a</mi>
      </math>, where
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>a</mi>
      </math>
      is the radius of the tube and
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>
      is the additional length for the baffled condition. For a non-cylindrical ducts, an equivalent radius can be calculated using
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>a</mi>
         <mo class=MathClass-rel>=</mo>
         <msqrt>
            <mrow>
               <mi>S</mi>
               <mo class=MathClass-bin>∕</mo>
               <mi>π</mi>
            </mrow>
         </msqrt>
      </math>.
   <li class=itemize>
      The tube is radiating in free space: in that case,
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mi>c</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mi>f</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mn>0</mn>
         <mo class=MathClass-punc>.</mo>
         <mn>61</mn>
         <mi>a</mi>
      </math>, where
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>
      is the additional length for the free space condition. We can see that without a baffle, the length correction is shorter, so there is less air moving outside the tube than with a baffle.
</ul>
<p>
   Thus, for a tube baffled on one side and free on the other, the length
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   to be used for the calculation of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   is equal to
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>l</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>l</mi>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>l</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>l</mi>
               </mrow>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(14)
</table>
<p>
   For other geometries than the baffled or free case, analytical formulas giving the length correction are not easy to derive. In that case, numerical modelling such as finite or boundary element methods could be used to get a reliable value. Nevertheless, in many real-life situations,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
   </math>
   can be used as approximate values depending on the geometry of the problem under consideration.<br class=newline>Note that open tubes are important parts of bass-reflex systems, also known as vented enclosures, which will be studied later in the course.
<h4 class=subsectionHead>Acoustic resistances</h4>
<p>
   Let’s now look at the capillary tube, which is a tube with a very thin diameter compared to the wavelength. In this case, the walls are always very close, and the friction on the walls is the main effect. This effect can be described using the following acoustic impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>in</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi>j</mi>
      <msub>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>. For a cylindrical tube of radius
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>a</mi>
   </math>, its value is given by
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>in</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>8</mn>
                  <mi mathvariant=italic>ηl</mi>
               </mrow>
               <mrow>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                     <mrow>
                        <mn>4</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>jω</mi>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>l</mi>
               </mrow>
               <mrow>
                  <mn>3</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(15)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>L</mi>
   </math>
   is the length of the tube,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>a</mi>
   </math>
   its radius and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>η</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>7</mn>
      <mo class=MathClass-bin>×</mo>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>5</mn>
         </mrow>
      </msup>
   </math>
   Pa.s is the air viscosity. This formula is valid for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>a</mi>
      <mo class=MathClass-rel><</mo>
      <mfrac>
         <mrow>
            <mn>0</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>002</mn>
         </mrow>
         <mrow>
            <msqrt>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
   </math>. Note that for a very small value of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>a</mi>
   </math>
   and at low frequencies, we have
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>in</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>≃</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>.<br class=newline>There are other systems that can result in low frequency acoustical resistance:
<ul class=itemize1>
   <li class=itemize>
      Slits, whose impedance is given by
      <table class=equation>
         <tr>
            <td style="font-size: 14px;">
               <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
                  <msubsup>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>in</mi>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-rel>=</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>=</mo>
                  <mfrac>
                     <mrow>
                        <mn>12</mn>
                        <mi mathvariant=italic>ηl</mi>
                     </mrow>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>h</mi>
                           </mrow>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                        </msup>
                        <mi>w</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jω</mi>
                  <mfrac>
                     <mrow>
                        <mn>6</mn>
                        <msub>
                           <mrow>
                              <mi>ρ</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mi>l</mi>
                     </mrow>
                     <mrow>
                        <mn>5</mn>
                        <mi mathvariant=italic>hw</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-punc></mo>
               </math>
            <td class=eq-no>(16)
      </table>
      <p>
         for a slit of height
         <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>h</mi>
         </math>
         , length
         <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>l</mi>
         </math>
         and width
         <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>w</mi>
         </math>. This formula is valid for
         <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>h</mi>
            <mo class=MathClass-rel><</mo>
            <mfrac>
               <mrow>
                  <mn>0</mn>
                  <mo class=MathClass-punc>.</mo>
                  <mn>003</mn>
               </mrow>
               <mrow>
                  <msqrt>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                  </msqrt>
               </mrow>
            </mfrac>
         </math>.
   <li class=itemize>Fabrics, weaves, tight grids, micro-perforated plates, etc.
   <li class=itemize>Porous materials such as glass wool, felts, plastic foams, etc. can be modelled at low frequencies by an acoustical resistance. For a more accurate, broadband description of these materials, models such as those of Delany and Bazley, Miki, Johnson-Champoux-Allard, Biot, etc. can be used. However, these models require additional material parameters such as the flow resistivity, tortuosity, etc.
</ul>
<p>We have also seen how to replace the description of the behaviour of spatially distributed acoustic systems with discrete elements that approximate the behaviour of the distributed system at low frequencies, <em><span class=cmti-10>i.e.</span></em> as long as the wavelength is greater than the dimensions of the system considered. This type of description is called my lumped-element modelling.
<h3 class=sectionHead>Helmholtz resonator</h3>
<p>
   The Helmholtz resonator is a 1-DOF acoustic resonator. It consists of an enclosure of volume
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
   </math>
   communicating with the outside air through a tube of physical length
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>l</mi>
   </math>
   and section
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   (see <a href="#" data-img-alt="Figure 6. Helmholtz resonator." data-img-src=${config.ASSETS_URL}/04/04/theory/figure-6.svg>Figure 6</a>). In the low frequency approximation, the tube, open at both ends, is equivalent to an acoustic mass
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   while the volume is equivalent to an acoustic compliance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>. The losses, mainly due to the rapid section changes at the tube ends and to friction on the tube walls, are considered to be proportional to the acoustic flow rate and are therefore equivalent to an acoustic resistance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>. Thus, the air in the neck acts as a solid mass that bounces off the spring that is the air trapped in the volume. We thus have the acoustic equivalent of a damped mass-spring system.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 6. Helmholtz resonator." src=${config.ASSETS_URL}/04/04/theory/figure-6.svg><figcaption class=caption>Figure 6. Helmholtz resonator.</figcaption></figure>
<p>
   Therefore, the behaviour of a Helmholtz resonator, excited by a sound wave of pressure
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
   </math>, can be represented by the equivalent electrical circuit shown in <a href="#" data-img-alt="Figure 7. Electrical circuit equivalent to the Helmholtz resonator." data-img-src=${config.ASSETS_URL}/04/04/theory/figure-7.svg>Figure 7</a>. It has the shape of a RLC series circuit and therefore a resonance frequency given by
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>S</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>l</mi>
                           </mrow>
                           <mrow>
                              <mi>p</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>V</mi>
                           </mrow>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(17)
</table>
<p>
   Note that the length
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   includes the end-length correction and is given by Eq. (14) because some amount of air around the openings is moving along with the air inside the neck. In the case of <a href="#" data-img-alt="Figure 6. Helmholtz resonator." data-img-src=${config.ASSETS_URL}/04/04/theory/figure-6.svg>Figure 6</a>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   is chosen for the termination connected to the volume and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
   </math>
   on the other side giving
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>l</mi>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
   </math>.
   <figure class=figure>
<p><img alt="Figure 7. Electrical circuit equivalent to the Helmholtz resonator." src=${config.ASSETS_URL}/04/04/theory/figure-7.svg><figcaption class=caption>Figure 7. Electrical circuit equivalent to the Helmholtz resonator.</figcaption></figure>
<h3 class=sectionHead>High frequency behaviour</h3>
<p>
   For higher frequencies, the approximations made above can no longer be used. However, if
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   is no longer small compared to the wavelength but the radius of the duct remains small, it is possible to use Eq. (8) to calculate the input impedance of the duct under consideration. When excited by incident pressure at one end, the duct volume velocity response is proportional to the inverse of the input impedance (<em><span class=cmti-10>i.e.</span></em> proportional to the input admittance). <a href="#" data-img-alt="Figure 8. Input admittance of a closed (top) and open duct (bottom)." data-img-src=${config.ASSETS_URL}/04/04/theory/figure-8.svg>Figure 8</a> shows the response of a duct for two configurations: when the other end is open or closed.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 8. Input admittance of a closed (top) and open duct (bottom)." src=${config.ASSETS_URL}/04/04/theory/figure-8.svg><figcaption class=caption>Figure 8. Input admittance of a closed (top) and open duct (bottom).</figcaption></figure>
<p>
   It can be seen that up to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>k</mi>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>, the low frequency approximation gives an accurate estimate of the admittance. For instance for a tube of length
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>20</mn>
   </math>
   cm, the approximation will be valid up to a frequency of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mstyle class=text>
               <mtext>max</mtext>
            </mstyle>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>c</mi>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <mi>π</mi>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>272</mn>
   </math>
   Hz calculated from
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>k</mi>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>2</mn>
            <mi>π</mi>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mstyle class=text>
                     <mtext>max</mtext>
                  </mstyle>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>l</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>.<br class=newline>However, the two curves separate for higher values of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>k</mi>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>. Moreover, we notice very strong variations of the admittance on the curve without approximation which passes by successive maxima and minima: they are the resonances and antiresonances of the duct. Their values are given by
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>nc</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(18)
</table>
<p>
   for an open tube at both ends, where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>n</mi>
   </math>
   is a positive integer, and
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mn>2</mn>
                  <mi>n</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mn>1</mn>
                  <mo class=MathClass-close>)</mo>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <msub>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(19)
</table>
<p>
   for the tube closed at one end. Remember that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   includes the appropriate length corrections (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>0</mn>
   </math>
   for a closed termination and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   or
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
   </math>
   for a baffled of free end).<br class=newline>As the frequency increases further, the wavelength becomes small compared to the longitudinal and transverse dimensions of the ducts. The propagation of sound then depends on the three coordinates of space, and resonances depending on the dimensions along these three axes appear. Their modelling can however still be done analytically for the simplest geometries but must be done numerically for complex shapes.
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, simple formulas to account for the behaviour of open and closed ducts at low frequencies were presented. These formulas will be very useful when it comes to model elements such as tubes, loudspeaker cabinets, etc. which are very often used in electroacoustic systems. Equivalent electrical circuits of simple elements have been given and the limits of use of these approximations have been illustrated.
<h3 class=likesectionHead>Glossary</h3>
<p>
<dl>
   <dt>1-DOF
   <dd>1 Degree Of Freedom.
   <dt>dB
   <dd>decibel.
   <dt>RLC
   <dd>Resistor, Inductor, Capacitor.
   <dt>RMS
   <dd>Root Mean Square.
   <dt>SPL
   <dd>Sound Pressure Level.
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-Beranek2012>[1]
   <dd class=thebibliography id=bib-1>
      <p>Leo Beranek and Tim Mellow. <span class=cmti-10>Acoustics: sound fields and transducers</span>. Academic Pres, 2012. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780123914866.
   <dt class=thebibliography id=X0-Kleiner2013>[2]
   <dd class=thebibliography id=bib-2>
      <p>Mendel Kleiner. <span class=cmti-10>Electroacoustics</span>. CRC Press, 2013. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780429073519.
   <dt class=thebibliography id=X0-Rossi1988>[3]
   <dd class=thebibliography id=bib-3>
      <p>Mario Rossi. <span class=cmti-10>Acoustics and Electroacoustics</span>. Artech House, 1988. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-0890062555.
</dl>`;

export default theory;
